import { formatNumber } from 'utils/number';

/**
 * Formats an interest at 8 decimals — the precision these totals are compared
 * at, and the precision an over-allocation is argued at.
 *
 * @param {number | null | undefined} value - The interest value.
 * @return {string} The formatted interest, or a placeholder when absent.
 */
export const interest = (value: number | null | undefined) =>
  formatNumber(value ?? null, 8, 8, 'decimal', true);

/**
 * Formats acreage at 2 decimals, matching the Bundle of Sticks columns and the
 * CRM screens these figures are reconciled against.
 *
 * @param {number | null | undefined} value - The acreage value.
 * @return {string} The formatted acreage, or a placeholder when absent.
 */
export const acres = (value: number | null | undefined) =>
  formatNumber(value ?? null, 2, 2, 'decimal', true);

/**
 * Formats a whole count with thousand separators.
 *
 * @param {number | null | undefined} value - The count.
 * @return {string} The formatted count, or a placeholder when absent.
 */
export const count = (value: number | null | undefined) =>
  formatNumber(value ?? null, 0, 0, 'decimal', true);

/**
 * Formats an interest total as a percentage. The stored value is a fraction
 * where 1.0 = 100%, so it is multiplied by 100 and suffixed with "%". Held at 6
 * decimals so the full 8-decimal precision an over-allocation is argued at
 * survives the shift, and thousand-separated because these sums run well past
 * 100% (a parcel claimed 78× over reads as 7,844.76%).
 *
 * @param {number | null | undefined} value - The interest fraction.
 * @return {string} The formatted percentage, or a placeholder when absent.
 */
export const interestPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '--';
  return `${formatNumber(value * 100, 6, 6, 'decimal', true)}%`;
};
